import { useAppKitAccount } from "@reown/appkit/react";
import { Address } from "viem";
import { useReadContracts } from "wagmi";

import { PLUME_STAKING_ABI } from "@/network/abi/plume-staking";

export const usePlumeStaking = () => {
  const { address } = useAppKitAccount();
  const PLUME_TOKEN = "0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE" as Address;
  const USDC_TOKEN = "0x81A7A4Ece4D161e720ec602Ad152a7026B82448b" as Address;

  // TODO: replace this with actual price from context / hook
  const PLUME_USD_PRICE = 0.2;

  const { data, isLoading, refetch } = useReadContracts({
    contracts: [
      // Claimable rewards
      {
        address: import.meta.env.VITE_PLUME_STAKING_CONTRACT_ADDRESS,
        abi: PLUME_STAKING_ABI,
        functionName: "getClaimableReward",
        args: address ? [address as Address, PLUME_TOKEN] : undefined,
      },
      // Claimable rewards
      {
        address: import.meta.env.VITE_PLUME_STAKING_CONTRACT_ADDRESS,
        abi: PLUME_STAKING_ABI,
        functionName: "getClaimableReward",
        args: address ? [address as Address, USDC_TOKEN] : undefined,
      },
      // PLUME reward rate
      {
        address: import.meta.env.VITE_PLUME_STAKING_CONTRACT_ADDRESS,
        abi: PLUME_STAKING_ABI,
        functionName: "rewardRate",
        args: [PLUME_TOKEN],
      },
      // USDC reward rate
      {
        address: import.meta.env.VITE_PLUME_STAKING_CONTRACT_ADDRESS,
        abi: PLUME_STAKING_ABI,
        functionName: "rewardRate",
        args: [USDC_TOKEN],
      },
    ],
    query: {
      enabled: !!address,
      refetchOnMount: false,
    },
  });

  // Extract results from data array
  const plumeStakingRewardAmountPlumeRaw = data?.[0]?.result;
  const plumeStakingRewardAmountUsdcRaw = data?.[1]?.result;
  const plumeRewardRate = data?.[2]?.result;
  const usdcRewardRate = data?.[3]?.result;

  // Calculate each individual reward rate
  const plumeRewardApr =
    ((Number(plumeRewardRate) || 0) / Number(10 ** 18)) * 365 * 24 * 60 * 60;
  const plumeRewardApy = (1 + plumeRewardApr / 365) ** 365 - 1;
  const usdcRewardApr =
    ((Number(usdcRewardRate) || 0) / Number(10 ** 18)) * 365 * 24 * 60 * 60;
  const usdcRewardApy = (1 + usdcRewardApr / 365) ** 365 - 1;

  // Calculate blended reward rate
  const blendedRewardApr =
    (Number(plumeRewardApr) || 0) +
    (Number(usdcRewardApr) || 0) / PLUME_USD_PRICE;
  const blendedRewardApy = (1 + blendedRewardApr / 365) ** 365 - 1;

  // Calculate each individual reward amount
  const plumeStakingRewardAmountPlume =
    (Number(plumeStakingRewardAmountPlumeRaw) || 0) / Number(10 ** 18);
  const plumeStakingRewardAmountUsdc =
    (Number(plumeStakingRewardAmountUsdcRaw) || 0) / Number(10 ** 6); // 6 decimals

  // Calculate total reward amount in equivalent Plume
  const plumeStakingRewardAmountTotalEquivInPlume =
    plumeStakingRewardAmountPlume +
    plumeStakingRewardAmountUsdc / PLUME_USD_PRICE;

  return {
    plumeStakingRewardAmountPlume,
    plumeStakingRewardAmountUsdc,
    plumeStakingRewardAmountTotalEquivInPlume,
    plumeRewardApy,
    usdcRewardApy,
    blendedRewardApy,
    isLoadingPlumeStaking: isLoading,
    PLUME_USD_PRICE,
    refetchPlumeStaking: refetch,
  };
};
